import { SearchOutlined } from '@ant-design/icons';
import { Input, Layout, Typography } from 'antd';
import React, { useMemo, useState } from 'react';

import { useAuthStore } from '@herond/shared/auth/useAuthStore';
import { useAntdToken } from '@herond/shared/styles';

import { BackgroundDashboard } from '@assets';

import { dashboardItems } from '../Dashboard/Config';
import DashboardItem from './components/DashboardItem';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const { token } = useAntdToken();
  const [search, setSearch] = useState<string>('');
  const [activeCategory] = useState<string>('all');

  const filteredDashboardItems = useMemo(() => {
    let filtered = dashboardItems.filter((item) => {
      if (!item.allowedRoles) return true;
      if (!user) return false;
      return item.allowedRoles.some((role) => user.roles.includes(role));
    });

    if (search.trim()) {
      filtered = filtered.filter((item) => item.label.toLowerCase().includes(search.toLowerCase()));
    }

    return filtered;
  }, [user, activeCategory, search]);

  return (
    <Layout.Content
      className="host-relative host-min-h-screen host-bg-cover host-bg-center host-bg-no-repeat"
      style={{ backgroundImage: `url(${BackgroundDashboard})` }}
    >
      {/* Uniform scrim over the full photo — keeps text readable without hiding the image */}
      <div aria-hidden className="host-absolute host-inset-0 host-pointer-events-none" style={{ background: 'rgba(8,12,24,0.4)' }} />

      <div className="host-relative host-z-10 host-px-6 host-pt-14 host-pb-12 host-max-w-7xl host-mx-auto">
        {/* Header Section */}
        <div className="host-mb-10 host-text-center">
          <Typography.Title level={2} className="!host-mb-1 !host-tracking-tight" style={{ color: '#ffffff' }}>
            {getGreeting()}{user?.name || user?.username ? `, ${user.name || user.username}` : ''}
          </Typography.Title>
          <Typography.Text className="host-text-base" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Choose an application to get started
          </Typography.Text>

          <div className="host-max-w-xl host-mx-auto host-mt-6">
            <Input
              size="large"
              placeholder="Search applications…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
              allowClear
              className="!host-rounded-full !host-px-5"
              style={{ boxShadow: token.boxShadowTertiary, background: token.colorBgContainer }}
            />
          </div>
        </div>

        {/* Apps Grid */}
        {filteredDashboardItems.length > 0 ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 20,
            }}
          >
            {filteredDashboardItems.map((item) => (
              <DashboardItem key={item.key} item={item} />
            ))}
          </div>
        ) : (
          <div className="host-text-center host-py-4">
            <div
              className="host-w-16 host-h-16 host-rounded-full host-flex host-items-center host-justify-center host-mx-auto host-mb-4"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <SearchOutlined style={{ color: '#ffffff', fontSize: 24 }} />
            </div>
            <Typography.Title
              level={4}
              className="!host-mb-2"
              style={{ color: '#ffffff', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
            >
              No application found
            </Typography.Title>
          </div>
        )}
      </div>
    </Layout.Content>
  );
};

export default React.memo(DashboardPage);
